import React, { useState } from 'react'
import { Plus, X, Edit2, Check } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { designTokens } from '@/utils/design-tokens'

export interface KeyNameItem {
  id?: string
  key: string
  name: string
}

interface KeyNameArrayInputProps {
  label: string
  values: KeyNameItem[]
  onChange: (values: KeyNameItem[]) => void
  keyPlaceholder?: string
  namePlaceholder?: string
  disabled?: boolean
  className?: string
  keyOnly?: boolean  // When true, only show key input and auto-set name = key
}

const KeyNameArrayInput: React.FC<KeyNameArrayInputProps> = ({
  label,
  values,
  onChange,
  keyPlaceholder = "키를 입력해주세요",
  namePlaceholder = "이름을 입력해주세요",
  disabled = false,
  className = '',
  keyOnly = false
}) => {
  const [newKey, setNewKey] = useState('')
  const [newName, setNewName] = useState('')
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingKey, setEditingKey] = useState('')
  const [editingName, setEditingName] = useState('')

  const handleAdd = () => {
    const trimmedKey = newKey.trim()
    const trimmedName = keyOnly ? trimmedKey : newName.trim()
    if (trimmedKey && trimmedName && !values.some(v => v.key === trimmedKey)) {
      onChange([...values, { key: trimmedKey, name: trimmedName }])
      setNewKey('')
      setNewName('')
    }
  }

  const handleRemove = (index: number) => {
    const updatedValues = values.filter((_, i) => i !== index)
    onChange(updatedValues)
  }

  const handleEdit = (index: number) => {
    setEditingIndex(index)
    setEditingKey(values[index].key)
    setEditingName(values[index].name)
  }

  const handleSaveEdit = (index: number) => {
    const trimmedKey = editingKey.trim()
    const trimmedName = keyOnly ? trimmedKey : editingName.trim()
    if (trimmedKey && trimmedName && !values.some((v, i) => i !== index && v.key === trimmedKey)) {
      const updatedValues = [...values]
      updatedValues[index] = { ...updatedValues[index], key: trimmedKey, name: trimmedName }
      onChange(updatedValues)
    }
    setEditingIndex(null)
    setEditingKey('')
    setEditingName('')
  }

  const handleCancelEdit = () => {
    setEditingIndex(null)
    setEditingKey('')
    setEditingName('')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  const handleEditKeyPress = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveEdit(index)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEdit()
    }
  }

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {/* Label */}
      <label
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </label>

      {/* Add New Item */}
      <div className="flex gap-2">
        <div className="flex-1 flex gap-2">
          {!keyOnly && (
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={namePlaceholder}
              disabled={disabled}
              size="small"
              className="w-1/2"
            />
          )}
          <Input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={keyPlaceholder}
            disabled={disabled}
            size="small"
            className={keyOnly ? "w-full" : "w-1/2"}
          />
        </div>
        <Button
          size="small"
          color="light"
          onClick={handleAdd}
          disabled={disabled || !newKey.trim() || (!keyOnly && !newName.trim()) || values.some(v => v.key === newKey.trim())}
          className="flex items-center gap-1 px-3"
        >
          <Plus className="w-3 h-3" />
          추가
        </Button>
      </div>

      {/* Current Items */}
      {values.length > 0 && (
        <div className="flex flex-col gap-2">
          <div
            className="text-sm"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              color: designTokens.colors.gray[5]
            }}
          >
            현재 항목 ({values.length}개)
          </div>
          <div className="flex flex-col gap-2 max-h-40 overflow-y-auto">
            {values.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-2 border rounded"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  backgroundColor: editingIndex === index ? designTokens.colors.primary.light + '10' : designTokens.colors.gray[0]
                }}
              >
                {editingIndex === index ? (
                  // Edit mode
                  <>
                    <div className="flex-1 flex gap-2">
                      {!keyOnly && (
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyPress={(e) => handleEditKeyPress(e, index)}
                          size="small"
                          className="w-1/2"
                          placeholder={namePlaceholder}
                          autoFocus
                        />
                      )}
                      <Input
                        value={editingKey}
                        onChange={(e) => setEditingKey(e.target.value)}
                        onKeyPress={(e) => handleEditKeyPress(e, index)}
                        size="small"
                        className={keyOnly ? "w-full" : "w-1/2"}
                        placeholder={keyPlaceholder}
                        autoFocus={keyOnly}
                      />
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(index)}
                        disabled={disabled || !editingKey.trim() || (!keyOnly && !editingName.trim()) || values.some((v, i) => i !== index && v.key === editingKey.trim())}
                        className="p-1 rounded hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          color: designTokens.colors.success
                        }}
                        title="저장"
                      >
                        <Check className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={disabled}
                        className="p-1 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          color: designTokens.colors.gray[4]
                        }}
                        title="취소"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </>
                ) : (
                  // Display mode
                  <>
                    <div className="flex items-center gap-2 flex-1">
                      <div className="flex-1 flex gap-4">
                        {!keyOnly && (
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs"
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                color: designTokens.colors.gray[4]
                              }}
                            >
                              이름:
                            </span>
                            <span
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: designTokens.typography.fontSize['body-small'][0],
                                color: designTokens.colors.gray[6]
                              }}
                              title={item.name}
                            >
                              {item.name}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span
                            className="text-xs"
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              color: designTokens.colors.gray[4]
                            }}
                          >
                            키:
                          </span>
                          <span
                            className="font-mono"
                            style={{
                              fontFamily: 'monospace',
                              fontSize: designTokens.typography.fontSize['body-small'][0],
                              color: designTokens.colors.gray[6],
                              backgroundColor: designTokens.colors.gray[0],
                              padding: '2px 6px',
                              borderRadius: '3px'
                            }}
                            title={item.key}
                          >
                            {item.key}
                          </span>
                        </div>
                      </div>
                      {item.id && (
                        <span
                          className="text-xs px-2 py-1 rounded"
                          style={{
                            backgroundColor: designTokens.colors.primary.light + '20',
                            color: designTokens.colors.primary.DEFAULT,
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '10px'
                          }}
                          title={`ID: ${item.id}`}
                        >
                          기존
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleEdit(index)}
                        disabled={disabled}
                        className="p-1 rounded hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          color: designTokens.colors.primary.DEFAULT
                        }}
                        title="수정"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(index)}
                        disabled={disabled}
                        className="p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          color: designTokens.colors.error
                        }}
                        title="삭제"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Helper Text */}
      {values.length === 0 && (
        <div
          className="text-xs"
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            color: designTokens.colors.gray[4]
          }}
        >
          {keyOnly ? '키를 입력하고 추가 버튼을 눌러주세요.' : '이름과 키를 모두 입력하고 추가 버튼을 눌러주세요.'}
        </div>
      )}
    </div>
  )
}

export default KeyNameArrayInput